import React, {useEffect, useState} from "react";
import {Form, Modal} from "react-bootstrap";
import {useFormik} from "formik";
import * as Yup from "yup";
import {defaultSalesCustomer} from "../generic/InitialStates";
import {AGCText} from "../../generic-components/AGCText";
import {AGCTextArea} from "../../generic-components/AGCTextArea";
import {apiGenericCreate, apiGenericUpdate, genericGetOne, getAllOrgSettings} from "../generic/crud";
import {ENTITY_ID_SALES_CUSTOMER} from "../generic/Constants";
import {showToast, TOAST_SUCCESS, TOAST_FAILURE} from "../../utils/toast/Toast";
import {GstinSmartFill} from "../../generic-components/GstinSmartFill";
import {ShippingAddressesSection} from "./ShippingAddressesSection";

export function SalesCustomerAddEditDialog(props) {
    const {show, handleClose, id} = props;
    const [objectForSubmit, setObjectForSubmit] = useState(defaultSalesCustomer);
    const [isLoading, setIsLoading] = useState(false);
    const [forUpdate, setForUpdate] = useState(false);
    const [title, setTitle] = useState("Sales Customer");
    const [allowOpeningBalance, setAllowOpeningBalance] = useState(false);

    const entityName = "Sales Customer";

    // Define validation schema for the form
    const validationSchema = Yup.object({
        name: Yup.string().required("Customer Name is required"),
        phone: Yup.string().required("Phone number is required"),
        address: Yup.string(),
        pinCode: Yup.string().trim().required("Pin Code is required"),
        gstNumber: Yup.string(),
        panNumber: Yup.string(),
        billingName: Yup.string(),
        // Shipping addresses are optional as a list, but each row must have
        // both an address and a pin code.
        shippingAddresses: Yup.array()
            .nullable()
            .of(
                Yup.object().shape({
                    address: Yup.string().trim().required("Address is required"),
                    pinCode: Yup.string().trim().required("Pin Code is required"),
                })
            ),
    });

    const formik = useFormik({
        initialValues: objectForSubmit,
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit(salesCustomer, {resetForm}) {
            setIsLoading(true);
            salesCustomer.creditLimit = 0;

            if (!id) {
                apiGenericCreate(ENTITY_ID_SALES_CUSTOMER, salesCustomer).then(() => {
                    setIsLoading(false);
                    handleClose();
                    showToast(entityName + " is Created!", TOAST_SUCCESS);
                    resetForm();
                }).catch((error) => {
                    setIsLoading(false);
                    showToast(
                        "Error creating " + entityName + ": " + (error.message || "Unknown error"),
                        TOAST_FAILURE
                    );
                });
            } else {
                apiGenericUpdate(ENTITY_ID_SALES_CUSTOMER, salesCustomer, id).then(() => {
                    setIsLoading(false);
                    handleClose();
                    showToast(entityName + " is Updated!", TOAST_SUCCESS);
                    resetForm();
                }).catch((error) => {
                    setIsLoading(false);
                    showToast(
                        "Error updating " + entityName + ": " + (error.message || "Unknown error"),
                        TOAST_FAILURE
                    );
                });
            }
        }
    });

    useEffect(() => {
        getAllOrgSettings().then((res) => {
            const settings = res?.data || [];
            const s = settings.find(s => s.typeName === 'ALLOW_OPENING_BALANCE_ON_CLIENT');
            setAllowOpeningBalance(s ? (s.value === true || s.value === 'true') : false);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (id != null) {
            setForUpdate(true);
            setTitle("Edit " + entityName);
            setIsLoading(true);
            genericGetOne(ENTITY_ID_SALES_CUSTOMER, id).then(
                ({data}) => {
                    setObjectForSubmit(data);
                    setTitle("Update " + entityName);
                    setIsLoading(false);
                })
                .catch((error) => {
                    setIsLoading(false);
                    showToast(
                        "Error fetching " + entityName + " data: " + (error.message || "Unknown error"),
                        TOAST_FAILURE
                    );
                });
        } else {
            setForUpdate(false);
            setObjectForSubmit(defaultSalesCustomer);
            setTitle("New " + entityName);
        }
    }, [show]);

    // Fill the form from a public GST record (blank fetched values keep what's typed).
    const applyGstDetails = (details) => {
        const v = formik.values;
        formik.setValues({
            ...v,
            gstNumber: details.gstin || v.gstNumber,
            name: details.tradeName || details.legalName || v.name,
            billingName: details.legalName || v.billingName,
            address: details.address || v.address,
            pinCode: details.pincode || v.pinCode,
            panNumber: details.pan || v.panNumber,
        });
        showToast("Business details filled from GST records — please review", TOAST_SUCCESS);
    };

    return (
        <Modal
            size="lg"
            show={show}
            onHide={handleClose}
            aria-labelledby="example-modal-sizes-title-lg">
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="overlay">
                {!isLoading ? (
                    <>
                        <Form className="form form-label-right" onSubmit={formik.handleSubmit}>
                            <GstinSmartFill
                                value={formik.values.gstNumber}
                                onChange={(v) => formik.setFieldValue("gstNumber", v)}
                                onApply={applyGstDetails}
                            />

                            <div className="row mb-3">
                                <Form.Group className="col-lg-6">
                                    <AGCText
                                        formik={formik}
                                        name={"Customer Name"}
                                        placeHolder={"Enter Customer Name"}
                                        id={"name"}
                                        value={formik.values.name}
                                        required={true}
                                        showToolTip={false}
                                    />
                                </Form.Group>

                                <Form.Group className="col-lg-6">
                                    <AGCText
                                        formik={formik}
                                        name={"Billing Name"}
                                        placeHolder={"Enter Billing Name (Optional)"}
                                        id={"billingName"}
                                        value={formik.values.billingName}
                                        required={false}
                                        showToolTip={false}
                                    />
                                </Form.Group>
                            </div>

                            <div className="row mb-3">
                                <Form.Group className="col-lg-6">
                                    <AGCText
                                        formik={formik}
                                        name={"Phone"}
                                        placeHolder={"Enter Phone Number"}
                                        id={"phone"}
                                        value={formik.values.phone}
                                        required={true}
                                        showToolTip={false}
                                    />
                                </Form.Group>

                                <Form.Group className="col-lg-6">
                                    <AGCText
                                        formik={formik}
                                        name={"Pin Code"}
                                        placeHolder={"Enter Pin Code"}
                                        id={"pinCode"}
                                        value={formik.values.pinCode}
                                        required={true}
                                        showToolTip={false}
                                    />
                                </Form.Group>
                            </div>

                            <div className="row mb-3">
                                <Form.Group className="col-lg-12">
                                    <AGCTextArea
                                        formik={formik}
                                        name={"Address"}
                                        placeHolder={"Enter Full Address"}
                                        id={"address"}
                                        value={formik.values.address}
                                        rows={2}
                                        required={false}
                                        showToolTip={false}
                                    />
                                </Form.Group>
                            </div>

                            <div className="row mb-3">
                                <Form.Group className="col-lg-6">
                                    <AGCText
                                        formik={formik}
                                        name={"GST Number"}
                                        placeHolder={"Enter GST Number (Optional)"}
                                        id={"gstNumber"}
                                        value={formik.values.gstNumber}
                                        required={false}
                                        showToolTip={false}
                                    />
                                </Form.Group>

                                <Form.Group className="col-lg-6">
                                    <AGCText
                                        formik={formik}
                                        name={"PAN Number"}
                                        placeHolder={"Enter PAN Number (Optional)"}
                                        id={"panNumber"}
                                        value={formik.values.panNumber}
                                        required={false}
                                        showToolTip={false}
                                    />
                                </Form.Group>
                            </div>

                            {allowOpeningBalance && (
                                <div className="row mb-3">
                                    <Form.Group className="col-lg-6">
                                        <AGCText
                                            formik={formik}
                                            name={"Opening Balance"}
                                            placeHolder={"Enter Opening Balance (Optional)"}
                                            id={"openingBalance"}
                                            toolTip={"Enter the opening balance if this customer has prior transactions"}
                                            value={formik.values.openingBalance}
                                            inputType={"number"}
                                            required={false}
                                        />
                                    </Form.Group>
                                </div>
                            )}

                            <ShippingAddressesSection
                                formik={formik}
                                defaultCollapsed={forUpdate}
                            />

                            <Modal.Footer className="border-0 pb-0">
                                <div>
                                    <button
                                        type="button"
                                        className="btn btn-light btn-elevate"
                                        onClick={handleClose}>
                                        Cancel
                                    </button>
                                    <> </>
                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-elevate">
                                        Save
                                    </button>
                                </div>
                            </Modal.Footer>
                        </Form>
                    </>
                ) : (
                    <div className="text-center p-4">
                        <span>Please Wait, Fetching Information from Server</span>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
}